import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  Alert,
  Skeleton,
  Slider,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Style as StyleIcon,
  FormatColorFill as ColorIcon,
  SmartButton as PatternIcon,
  Category as CategoryIcon,
  LocalOffer as TagIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import { styleAPI } from '../utils/api';
import { debugLog } from '../utils/debug';

// Styled components
const PreferenceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: `0 18px 32px ${alpha(theme.palette.primary.main, 0.16)}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
    borderRadius: '4px 4px 0 0',
  },
}));

const ImportanceSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: 8,
  '& .MuiSlider-track': {
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    height: 20,
    width: 20,
    backgroundColor: '#fff',
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: '0 5px 10px rgba(0,0,0,0.2)',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: theme.palette.primary.main,
    borderRadius: '8px',
    padding: '4px 8px',
    fontWeight: 'bold',
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const SuggestionButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  margin: '4px',
  transition: 'all 0.2s ease',
  textTransform: 'none',
  fontWeight: 500,
  padding: '4px 12px',
  boxShadow: 'none',
  border: '1px solid rgba(0,0,0,0.08)',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: '#fff',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
  },
}));

const StyleMetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  height: '100%',
  background: `linear-gradient(145deg, #ffffff 0%, ${alpha(theme.palette.primary.light, 0.06)} 100%)`,
  boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 14px 30px ${alpha(theme.palette.primary.main, 0.13)}`,
  },
}));

// Common style preference categories
const PREFERENCE_CATEGORIES = [
  { value: 'colors', label: 'Colors', icon: <ColorIcon /> },
  { value: 'patterns', label: 'Patterns', icon: <PatternIcon /> },
  { value: 'styles', label: 'Styles', icon: <StyleIcon /> },
  { value: 'occasions', label: 'Occasions', icon: <CategoryIcon /> },
  { value: 'brands', label: 'Brands', icon: <TagIcon /> },
  { value: 'other', label: 'Other', icon: <StyleIcon /> },
];

// Example suggestions for each category
const SUGGESTIONS = {
  colors: ['Black', 'Navy Blue', 'White', 'Gray', 'Beige', 'Earth tones', 'Bright colors', 'Pastels'],
  patterns: ['Solid colors', 'Stripes', 'Checks', 'Plaid', 'Floral', 'Geometric', 'Minimal patterns'],
  styles: ['Casual', 'Formal', 'Business casual', 'Streetwear', 'Classic', 'Minimalist', 'Vintage', 'Athletic'],
  occasions: ['Work', 'Everyday', 'Formal events', 'Outdoor activities', 'Gym', 'Beach', 'Travel'],
  brands: ['Prefer sustainable brands', 'Luxury brands', 'Accessible fashion', 'Sportswear brands'],
  other: ['Comfortable fabrics', 'Natural materials', 'Easy care', 'Layerable pieces'],
};

const StyleProfile = () => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [colorAnalysis, setColorAnalysis] = useState([]);
  const [styleAnalysis, setStyleAnalysis] = useState([]);

  // Form state for adding/editing preferences
  const [formData, setFormData] = useState({
    category: '',
    preference: '',
    importance: 3, // Default middle value
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch user's style preferences
  useEffect(() => {
    fetchPreferences();
  }, []);

  // Fetch color and style analysis based on preferences and closet
  useEffect(() => {
    if (!preferences || preferences.length === 0) {
      setColorAnalysis([]);
      setStyleAnalysis([]);
      return;
    }

    try {
      // Simple algorithm to calculate a style metric based on keywords
      const calculateStyleMetric = (prefs, keywords) => {
        let score = 50; // Default middle value

        prefs.forEach(pref => {
          const prefLower = pref.preference.toLowerCase();

          // Check for positive keywords
          if (prefLower.includes(keywords[0]) || prefLower.includes(keywords[1])) {
            score += pref.importance * 5;
          }

          // Check for opposing keywords
          if (keywords[2] && (prefLower.includes(keywords[2]) ||
              (keywords[3] && prefLower.includes(keywords[3])))) {
            score -= pref.importance * 5;
          }
        });

        // Clamp value between 0-100
        return Math.max(0, Math.min(100, score));
      };

      // Extract color preferences
      const colorPrefs = preferences.filter(pref =>
        pref.category.toLowerCase() === 'colors' ||
        pref.preference.toLowerCase().includes('color')
      );

      setColorAnalysis(colorPrefs);

      // Extract style insights
      const styleInsights = [
        { label: 'Formality', value: calculateStyleMetric(preferences, ['formal', 'casual', 'business']) },
        { label: 'Boldness', value: calculateStyleMetric(preferences, ['bold', 'bright', 'subtle', 'minimal']) },
        { label: 'Trendiness', value: calculateStyleMetric(preferences, ['trendy', 'classic', 'timeless', 'vintage']) },
      ];

      setStyleAnalysis(styleInsights);
    } catch (err) {
      console.error('Error analyzing preferences:', err);
    }
  }, [preferences]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await styleAPI.getPreferences();
      debugLog('Style preferences response:', response);
      setPreferences(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching style preferences:', err);
      setError('Failed to load your style preferences. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (index = -1) => {
    if (index >= 0) {
      // Edit existing preference
      const pref = preferences[index];
      setFormData({
        category: pref.category,
        preference: pref.preference,
        importance: pref.importance
      });
      setEditIndex(index);
    } else {
      // Add new preference
      setFormData({
        category: '',
        preference: '',
        importance: 3
      });
      setEditIndex(-1);
    }
    
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleImportanceChange = (event, newValue) => {
    setFormData({
      ...formData,
      importance: newValue
    });
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData({
      ...formData,
      preference: suggestion
    });
    
    if (formErrors.preference) {
      setFormErrors({
        ...formErrors,
        preference: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    }
    
    if (!formData.preference.trim()) {
      errors.preference = 'Preference is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      if (editIndex >= 0) {
        // Update existing preference
        const prefId = preferences[editIndex].id;
        await styleAPI.updatePreference(prefId, formData);
      } else {
        // Add new preference
        await styleAPI.addPreference(formData);
      }
      
      // Refresh preferences list
      await fetchPreferences();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving preference:', err);
      setError('Failed to save preference. Please try again.');
    }
  };

  const handleDelete = async (index) => {
    try {
      const prefId = preferences[index].id;
      await styleAPI.deletePreference(prefId);
      
      // Refresh preferences list
      await fetchPreferences();
    } catch (err) {
      console.error('Error deleting preference:', err);
      setError('Failed to delete preference. Please try again.');
    }
  };

  const getCategoryIcon = (categoryName) => {
    const category = PREFERENCE_CATEGORIES.find(
      cat => cat.value === categoryName.toLowerCase() || cat.label.toLowerCase() === categoryName.toLowerCase()
    );
    
    return category ? category.icon : <StyleIcon />;
  };

  const getImportanceLabel = (value) => {
    const labels = {
      1: 'Not important',
      2: 'Somewhat important',
      3: 'Important',
      4: 'Very important',
      5: 'Essential'
    };
    
    return labels[value] || '';
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0E7BFF 0%, #00A5FF 100%)',
          color: 'white',
          boxShadow: '0 16px 30px rgba(14, 123, 255, 0.25)',
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <StyleIcon sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h4" fontWeight="600">
            Style Profile
          </Typography>
        </Box>
        <Typography variant="body1">
          Define your preferences so recommendations match your real style, not generic trends.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Style Analysis Section */}
      <Grid container spacing={4} sx={{ mb: 5 }}>
        <Grid item xs={12} md={6}>
          <StyleMetricCard>
            <Typography variant="h6" gutterBottom fontWeight="600" sx={{ 
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5
            }}>
              Your Style Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Based on your preferences, here's an analysis of your style profile.
            </Typography>
            
            {styleAnalysis.length > 0 ? (
              <Box>
                {styleAnalysis.map((metric, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" fontWeight={500}>
                        {metric.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {metric.value < 40 ? 'Low' : metric.value > 60 ? 'High' : 'Balanced'}
                      </Typography>
                    </Box>
                    <Box position="relative" width="100%" height={12} bgcolor="rgba(0,0,0,0.04)" borderRadius={6}>
                      <Box 
                        position="absolute"
                        left={0}
                        top={0}
                        height="100%"
                        width={`${metric.value}%`}
                        borderRadius={6}
                        sx={{
                          background: `linear-gradient(90deg, 
                            ${metric.label === 'Formality' ? '#0E7BFF' : 
                            metric.label === 'Boldness' ? '#FF6E4D' : '#00A5FF'} 0%, 
                            ${metric.label === 'Formality' ? '#4EA3FF' : 
                            metric.label === 'Boldness' ? '#FFA77F' : '#69C6FF'} 100%)`,
                          transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box py={3} textAlign="center">
                <Typography color="text.secondary">
                  Add more style preferences to see your analysis
                </Typography>
              </Box>
            )}
          </StyleMetricCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <StyleMetricCard>
            <Typography variant="h6" gutterBottom fontWeight="600" sx={{ 
              borderLeft: '4px solid',
              borderColor: 'secondary.main',
              pl: 2,
              py: 0.5
            }}>
              Color Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Colors you've indicated preference for in your style profile.
            </Typography>
            
            {colorAnalysis.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1.5} mt={3}>
                {colorAnalysis.map((pref, index) => (
                  <Chip
                    key={index}
                    label={pref.preference}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      py: 2.5,
                      background: `linear-gradient(135deg, ${pref.importance > 3 ? '#FF6E4D' : '#0E7BFF'} 20%, ${pref.importance > 3 ? '#FFA77F' : '#00A5FF'} 80%)`,
                      color: 'white',
                      boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 5px 10px rgba(0,0,0,0.15)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Box py={3} textAlign="center">
                <Typography color="text.secondary">
                  Add color preferences to see them here
                </Typography>
              </Box>
            )}
          </StyleMetricCard>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="600">
          Your Style Preferences
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 8,
            px: 3,
            py: 1.2,
            boxShadow: '0 8px 18px rgba(14, 123, 255, 0.2)',
            background: 'linear-gradient(90deg, #0E7BFF, #00A5FF)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 12px 22px rgba(14, 123, 255, 0.25)',
            },
          }}
        >
          Add Preference
        </Button>
      </Box>

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((idx) => (
            <Grid item xs={12} sm={6} md={idx <= 2 ? 6 : 4} key={idx}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Skeleton variant="text" width="50%" height={34} />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="rounded" height={72} sx={{ mt: 1.5, borderRadius: 2 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : preferences.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px dashed rgba(0,0,0,0.1)',
          }}
        >
          <StyleIcon sx={{ fontSize: 60, color: 'primary.light', mb: 2, opacity: 0.7 }} />
          <Typography variant="h6" gutterBottom>
            No style preferences yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add your style preferences to get better outfit recommendations.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenDialog()}
            sx={{
              mt: 2,
              borderRadius: 8,
              px: 3,
              py: 1.2,
            }}
          >
            Add Your First Preference
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {preferences.map((pref, index) => (
            <Grid item xs={12} sm={6} md={4} key={`pref-${index}`}>
              <PreferenceCard>
                <CardContent sx={{ pb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box display="flex" alignItems="center" mb={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          background: 'linear-gradient(135deg, rgba(14, 123, 255, 0.12) 0%, rgba(0, 165, 255, 0.12) 100%)',
                          color: 'primary.main',
                        }}
                      >
                        {getCategoryIcon(pref.category)}
                      </Box>
                      <CategoryChip
                        size="small"
                        label={PREFERENCE_CATEGORIES.find(c => c.value === pref.category)?.label || pref.category}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(index)}
                        sx={{ 
                          color: 'primary.main', 
                          bgcolor: 'rgba(14, 123, 255, 0.1)',
                          '&:hover': { bgcolor: 'rgba(14, 123, 255, 0.2)' } 
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(index)}
                        sx={{ 
                          ml: 1, 
                          color: 'error.main', 
                          bgcolor: 'rgba(239, 83, 80, 0.1)',
                          '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.2)' } 
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Typography variant="h6" sx={{ my: 2, fontWeight: 600 }}>
                    {pref.preference}
                  </Typography>
                  
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Importance
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight="600" 
                        sx={{ 
                          color: pref.importance > 3 ? 'secondary.main' : 'primary.main',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 10,
                          bgcolor: pref.importance > 3 ? 'rgba(255, 110, 77, 0.12)' : 'rgba(14, 123, 255, 0.12)',
                        }}
                      >
                        {getImportanceLabel(pref.importance)}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{
                        width: '100%',
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(0,0,0,0.05)',
                        mt: 1,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box 
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          height: '100%',
                          width: `${(pref.importance / 5) * 100}%`,
                          borderRadius: 3,
                          background: `linear-gradient(90deg, ${pref.importance > 3 ? '#FF6E4D' : '#0E7BFF'}, ${pref.importance > 3 ? '#FFA77F' : '#00A5FF'})`,
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </PreferenceCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Preference Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight="600">
            {editIndex >= 0 ? 'Edit Style Preference' : 'Add Style Preference'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="text.secondary" sx={{ mb: 3 }}>
            Define what you like and how important it is to your personal style.
          </DialogContentText>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.category}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  value={formData.category}
                  name="category"
                  label="Category"
                  onChange={handleInputChange}
                >
                  {PREFERENCE_CATEGORIES.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ mr: 1, color: 'primary.main' }}>{category.icon}</Box>
                        {category.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.category && (
                  <FormHelperText>{formErrors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {formData.category && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="500" color="text.secondary" gutterBottom>
                    Suggestions:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                    {SUGGESTIONS[formData.category] &&
                      SUGGESTIONS[formData.category].map((suggestion, i) => (
                        <SuggestionButton
                          key={i}
                          size="small"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </SuggestionButton>
                      ))}
                  </Box>
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Style Preference"
                name="preference"
                value={formData.preference}
                onChange={handleInputChange}
                error={!!formErrors.preference}
                helperText={formErrors.preference}
                placeholder="e.g., Minimalist style, Blue colors, etc."
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" fontWeight="500">
                    Importance
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="600" 
                    sx={{ 
                      color: formData.importance > 3 ? 'secondary.main' : 'primary.main',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 10,
                      bgcolor: formData.importance > 3 ? 'rgba(255, 110, 77, 0.12)' : 'rgba(14, 123, 255, 0.12)',
                    }}
                  >
                    {getImportanceLabel(formData.importance)}
                  </Typography>
                </Box>
                
                <ImportanceSlider
                  value={formData.importance}
                  min={1}
                  max={5}
                  step={1}
                  marks
                  onChange={handleImportanceChange}
                />
                
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="caption" color="text.secondary">Less Important</Typography>
                  <Typography variant="caption" color="text.secondary">Very Important</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ borderRadius: 8, px: 3 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            sx={{ 
              borderRadius: 8, 
              px: 3,
              background: 'linear-gradient(90deg, #0E7BFF, #00A5FF)',
            }}
          >
            {editIndex >= 0 ? 'Save Changes' : 'Add Preference'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StyleProfile; 
